'use client';

import { Component, type ErrorInfo, type PropsWithChildren } from 'react';

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
    };
  }
}

/**
 * Error boundary that reports the React component stack, retries, and only
 * escalates when crashing repeatedly.
 *
 * The recurring #310 crash arrives in PostHog with only minified React
 * runtime frames — the crashing component's identity never appears, and
 * PostHog's sourcemap resolver is blocked at the CDN. componentDidCatch is
 * the one place React hands over `errorInfo.componentStack` (the actual
 * component chain), so we capture it as a custom event.
 *
 * Recovery: the first two catches re-render children immediately (the crash
 * class is transient — a retry almost always paints). From the third catch
 * on, the error is rethrown to the route's error.tsx for the explicit
 * reload UI. Instance state (not sessionStorage) scopes the counter to this
 * mount, so a later independent crash gets fresh retries.
 */
interface CrashReporterState {
  error: Error | null;
}

export class CrashReporter extends Component<
  PropsWithChildren,
  CrashReporterState
> {
  override state: CrashReporterState = { error: null };
  private catches = 0;

  static getDerivedStateFromError(error: Error): CrashReporterState {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.catches += 1;

    try {
      window.posthog?.capture('react_crash_component_stack', {
        message: String(error?.message ?? error).slice(0, 500),
        component_stack: String(errorInfo?.componentStack ?? '').slice(0, 4000),
        path: window.location.pathname,
        catch_count: this.catches,
      });
    } catch {
      // reporting must never mask the real error
    }

    if (this.catches < 3) {
      // Transient render crash — retry the subtree right away.
      this.setState({ error: null });
    }
  }

  override render() {
    if (this.state.error) {
      if (this.catches >= 3) {
        // Persistent crash — hand off to the nearest error.tsx boundary.
        throw this.state.error;
      }
      // One blank frame while componentDidCatch schedules the retry.
      return null;
    }
    return this.props.children;
  }
}
